import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { get, ref } from 'firebase/database';
import { Building2, Loader2, MapPin, Users as UsersIcon } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { auth, database } from '../services/firebase';

type UserProfile = {
  displayName?: string;
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
};

type QuickLink = {
  to: string;
  label: string;
  description: string;
  icon: typeof Building2;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/dashboard' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      return;
    }
    const profileRef = ref(database, `users/${currentUser.uid}`);
    void get(profileRef)
      .then(snapshot => {
        const value = snapshot.val() as UserProfile | null;
        if (value?.needsOrganizationSetup) {
          navigate('/organization/create', {
            replace: true,
            state: { returnTo: '/dashboard' },
          });
          return;
        }
        setProfile(value);
      })
      .catch(error => {
        console.warn('Failed to load dashboard profile', error);
      });
  }, [currentUser, navigate]);

  const cardClass = `${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} rounded-lg p-8 ring-1`;
  const mutedTextClass = `${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`;
  const quickLinkCardClass = `${isDarkMode ? 'border-slate-800 bg-slate-900/50 hover:border-blue-500/60 hover:bg-slate-900/80' : 'border-slate-200 bg-white hover:border-blue-500/40 hover:bg-blue-50/50'} group flex h-full flex-col justify-between gap-3 rounded-2xl border px-5 py-4 transition`;
  const quickLinkDescriptionClass = `text-sm transition ${isDarkMode ? 'text-slate-300 group-hover:text-slate-200' : 'text-slate-500 group-hover:text-slate-600'}`;

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/dashboard' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/dashboard' } });
  };

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/dashboard' },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Preparing your dashboard...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const quickLinks: QuickLink[] = [
    {
      to: '/organization',
      label: 'Organization',
      description: 'Update clinic details or billing information.',
      icon: Building2,
    },
    {
      to: '/locations',
      label: 'Clinics',
      description: 'Review clinic locations and keep their details current.',
      icon: MapPin,
    },
    {
      to: '/users',
      label: 'Team',
      description: 'Invite teammates and manage their permissions.',
      icon: UsersIcon,
    },
  ];

  const displayName =
    profile?.displayName?.trim() ||
    currentUser.displayName?.trim() ||
    currentUser.email ||
    'there';
  const hasOrganizationAccess = Boolean(profile?.organizationId);

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      hasOrganizationAccess={hasOrganizationAccess}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
    >
      <div className="space-y-6">
        <section className={cardClass}>
          {/* Header Row */}
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">Welcome back, {displayName}!</h1>
          </div>

          {/* Subtitle below both */}
          <p className={`${mutedTextClass} mt-3`}>
            Use the quick links below to set up your clinic and invite your team.
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {quickLinks.map(({ to, label, description, icon: Icon }) => (
              <Link key={to} to={to} className={quickLinkCardClass}>
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500 group-hover:bg-blue-500 group-hover:text-white">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-base font-semibold">{label}</p>
                    <p className={quickLinkDescriptionClass}>
                      {description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
